import React, { useState, useRef, useEffect } from 'react';
import { CornerDownLeft, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface TextSelectionDialogProps {
  x: number;
  y: number;
  selectedText: string;
  onSubmit: (question: string) => void;
  onClose: () => void;
  isRTL?: boolean;
}

const TextSelectionDialog: React.FC<TextSelectionDialogProps> = ({
  x,
  y,
  selectedText,
  onSubmit,
  onClose,
  isRTL = false
}) => {
  const { t } = useTranslation();
  const [question, setQuestion] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Auto-focus the input when dialog opens
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  const handleSubmit = () => {
    if (question.trim()) {
      onSubmit(question.trim());
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  };

  // Calculate dialog position to ensure it stays within viewport
  const dialogStyle: React.CSSProperties = {
    left: isRTL ? 'auto' : `${Math.max(16, Math.min(x - 140, window.innerWidth - 300))}px`,
    right: isRTL ? `${Math.max(16, Math.min(x - 140, window.innerWidth - 300))}px` : 'auto',
    top: `${Math.max(16, y - 80)}px`,
    maxWidth: '300px',
    minWidth: '280px',
    pointerEvents: 'auto'
  };

  return (
    <>
      {/* Backdrop to close dialog */}
      <div 
        className="fixed inset-0 z-40 bg-black/10" 
        onClick={onClose}
      />
      
      {/* Dialog */}
      <div
        data-text-selection-ui="true"
        className="absolute z-50 bg-white rounded-lg shadow-2xl border border-surface-border p-4"
        style={dialogStyle}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`flex items-center justify-between mb-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
          <h3 className={`text-sm font-semibold text-surface-primary ${isRTL ? 'text-right' : ''}`}>
            {t('exams:practice.textSelection.inputTitle')}
          </h3>
          <button
            onClick={onClose}
            className="w-5 h-5 text-surface-muted hover:text-surface-primary transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Selected text preview */}
        <div className="mb-3 p-2 bg-surface-muted/10 rounded border-l-2 border-vibrant-orange text-xs text-surface-secondary max-h-16 overflow-y-auto">
          <div className={`italic ${isRTL ? 'text-right' : 'text-left'}`} dir={isRTL ? 'rtl' : 'ltr'}>
            "{selectedText.length > 100 ? selectedText.substring(0, 100) + '...' : selectedText}"
          </div>
        </div>

        {/* Input field */}
        <div className="relative">
          <input
            ref={inputRef}
            type="text"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            onKeyDown={handleKeyPress}
            placeholder={t('exams:practice.textSelection.inputPlaceholder')}
            className={`w-full px-3 py-2 border border-surface-border rounded-md focus:outline-none focus:ring-2 focus:ring-vibrant-orange/50 focus:border-transparent text-sm ${
              isRTL ? 'text-right pr-10 pl-3' : 'text-left pl-3 pr-10'
            }`}
            dir={isRTL ? 'rtl' : 'ltr'}
          />
          
          {/* Submit button */}
          <button
            onClick={handleSubmit}
            disabled={!question.trim()}
            className={`absolute top-1/2 transform -translate-y-1/2 ${
              isRTL ? 'left-2' : 'right-2'
            } w-6 h-6 text-vibrant-orange hover:text-vibrant-orange/80 disabled:text-surface-muted transition-colors`}
            title={t('exams:practice.textSelection.sendQuestion')}
          >
            <CornerDownLeft className="w-4 h-4" />
          </button>
        </div>

        {/* Hint text */}
        <div className={`mt-2 text-xs text-surface-muted ${isRTL ? 'text-right' : ''}`}>
          {t('common:actions.pressEnterToSend', 'Press Enter to send')}
        </div>
      </div>
    </>
  );
};

export default TextSelectionDialog; 